import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactNode, type JSX } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import ThemeProvider from '@mui/material/styles/ThemeProvider'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ApiError, isSessionExpired } from './api'
import { AuthShell } from './auth'
import { AppLayout } from './components/AppLayout'
import { ErrorPanel, LoadingPanel } from './components/States'
import { loadRuntimeConfig, type RuntimeConfig } from './config'
import { DashboardPage } from './pages/DashboardPage'
import { ReviewsPage } from './pages/ReviewsPage'
import { OutreachPage } from './pages/OutreachPage'
import { NewJobPage } from './pages/NewJobPage'
import { SettingsPage } from './pages/SettingsPage'
import { SignInPage } from './pages/SignInPage'
import { UsersPage } from './pages/UsersPage'
import { buildTheme } from './theme'
import { ErrorBoundary } from './components/ErrorBoundary'

/*
 * The four screens that draw the data grid, fetched when one is opened.
 *
 * The grid is its own chunk on purpose — 244 kB gzipped, close to three times
 * the rest of the application — and importing it up here made every route
 * preload it before first paint, including the dashboard, settings, the new-job
 * wizard and the sign-in page, none of which draw a grid at all.
 */
const CompaniesPage = lazy(() => import('./pages/CompaniesPage').then((m) => ({ default: m.CompaniesPage })))
const ContactsPage = lazy(() => import('./pages/ContactsPage').then((m) => ({ default: m.ContactsPage })))
const JobDetailPage = lazy(() => import('./pages/JobDetailPage').then((m) => ({ default: m.JobDetailPage })))
const JobsListPage = lazy(() => import('./pages/JobsListPage').then((m) => ({ default: m.JobsListPage })))
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })))
const RepairsPage = lazy(() => import('./pages/RepairsPage').then((m) => ({ default: m.RepairsPage })))

const queryDefaults = {
  queries: {
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: (failureCount: number, error: unknown) => {
      // A 4xx does not become a 200 by asking again; retrying only buries the
      // real message (an expired token, a missing job) behind three timeouts.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  },
  mutations: { retry: false },
}

/**
 * The query client, and the one failure no Retry button can fix.
 *
 * A 401 means the API rejected the token we hold — the week-long refresh token
 * lapsed, or an administrator revoked the session or disabled the account.
 * Nothing in the OIDC library notices: a failed silent renew leaves the user
 * object in place, so `isAuthenticated` stayed true and the console went on
 * rendering with a dead token. Every panel red, every Retry sending it again,
 * and the only way back in was to guess that "Sign out" was also the way to sign
 * in. Dropping the user is what flips `isAuthenticated` false, and AuthGate then
 * shows the sign-in page.
 */
function QueryProvider({ children }: Readonly<{ children: ReactNode }>): JSX.Element {
  const auth = useAuth()
  // The caches outlive every render, so the handler reaches for the auth of the
  // moment rather than the one it happened to be built with.
  const current = useRef(auth)
  current.current = auth

  const [client] = useState(() => {
    const endSession = (error: unknown): void => {
      if (isSessionExpired(error)) void current.current.removeUser()
    }
    return new QueryClient({
      defaultOptions: queryDefaults,
      queryCache: new QueryCache({ onError: endSession }),
      mutationCache: new MutationCache({ onError: endSession }),
    })
  })

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}

function CentredMessage({ children }: Readonly<{ children: ReactNode }>): JSX.Element {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Box sx={{ maxWidth: 560, width: '100%' }}>{children}</Box>
    </Box>
  )
}

/** Gate on authentication before any route renders: every route needs a token. */
function AuthGate({ children }: Readonly<{ children: ReactNode }>): JSX.Element {
  const auth = useAuth()

  if (auth.isLoading) return <CentredMessage>{<LoadingPanel label="Checking your session" />}</CentredMessage>
  if (!auth.isAuthenticated) return <SignInPage error={auth.error} />
  return <>{children}</>
}

/** A route whose page pulls the grid chunk in behind it. */
function GridRoute({ where, children }: Readonly<{ where: string; children: ReactNode }>): JSX.Element {
  return (
    <ErrorBoundary where={where}>
      <Suspense fallback={<LoadingPanel />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        {/*
          The front page is the dashboard, and Jobs has a URL of its own.
          Jobs used to be the index, which meant the first thing anybody saw was
          a list of runs rather than the state of the product — and it left
          "/jobs" as a path that existed only as a prefix and 404'd on its own.
        */}
        <Route
          index
          element={
            <ErrorBoundary where="Dashboard">
              <DashboardPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="jobs"
          element={
            <GridRoute where="Jobs">
              <JobsListPage />
            </GridRoute>
          }
        />
        <Route
          path="jobs/new"
          element={
            <ErrorBoundary where="New job">
              <NewJobPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="jobs/:jobId"
          element={
            <GridRoute where="Job detail">
              <JobDetailPage />
            </GridRoute>
          }
        />
        <Route
          path="companies"
          element={
            <GridRoute where="Companies">
              <CompaniesPage />
            </GridRoute>
          }
        />
        <Route
          path="contacts"
          element={
            <GridRoute where="Contacts">
              <ContactsPage />
            </GridRoute>
          }
        />
        <Route
          path="reviews"
          element={
            <ErrorBoundary where="Review">
              <ReviewsPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="repairs"
          element={
            <GridRoute where="Repairs">
              <RepairsPage />
            </GridRoute>
          }
        />
        <Route
          path="outreach"
          element={
            <ErrorBoundary where="Outreach">
              <OutreachPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="audit"
          element={
            <GridRoute where="Audit log">
              <AuditPage />
            </GridRoute>
          }
        />
        <Route
          path="users"
          element={
            <ErrorBoundary where="Users">
              <UsersPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="settings"
          element={
            <ErrorBoundary where="Settings">
              <SettingsPage />
            </ErrorBoundary>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

export function App(): JSX.Element {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const theme = useMemo(() => buildTheme(prefersDark ? 'dark' : 'light'), [prefersDark])

  const [config, setConfig] = useState<RuntimeConfig | null>(null)
  const [configError, setConfigError] = useState<unknown>(null)

  useEffect(() => {
    let cancelled = false
    loadRuntimeConfig().then(
      (loaded) => {
        if (!cancelled) setConfig(loaded)
      },
      (error: unknown) => {
        if (!cancelled) setConfigError(error)
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  /*
   * Nothing can render until the runtime config has arrived: the auth shell
   * needs the Cognito details and the query client needs the API origin, so a
   * partial render here would be a broken sign-in rather than a slow one.
   */
  const shell = (): JSX.Element => {
    if (configError) {
      return (
        <CentredMessage>
          <ErrorPanel error={configError} onRetry={() => window.location.reload()} />
        </CentredMessage>
      )
    }
    if (!config) {
      return (
        <CentredMessage>
          <LoadingPanel label="Starting" />
        </CentredMessage>
      )
    }
    return (
      <AuthShell config={config}>
        <QueryProvider>
          <AuthGate>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthGate>
        </QueryProvider>
      </AuthShell>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {shell()}
    </ThemeProvider>
  )
}
